import { EProductCardForm } from '@/domain/product/EProductCardForm';
import { EProductCategory } from '@/domain/product/EProductCategory';
import { EProductFormat } from '@/domain/product/EProductFormat';
import type { ICuttingShapeRepository } from '@/application/ports/ICuttingShapeRepository';
import type { Product } from '@/domain/product/Product';
import type { IProductStateRepository } from '@/application/ports/IProductStateRepository';
import type { TProductMeshParams } from '@/application/ports/TProductMeshParams';

// What no port carries: the identifier the visitor typed in the URL, and a way to turn a file
// id into a URL.
type TProductMeshContext = {
    // Injected rather than built here: prefixing VITE_API_URL is an api/ concern, and taking it
    // as a function is what keeps this callable from a test with no env.
    fileUrl: (fileId: string) => string;
    // NOT product.name. config.store keeps the two apart on purpose — the backend may
    // canonicalise or alias, and GobeletStrategy reads this one to pick its GLB. Passing
    // product.name here would silently change which model loads.
    urlProductType: string;
};

// The ports the state is read through, passed in rather than reached for — so useEditor3D, which
// only wires things, can be exercised with fakes and needs no Pinia.
type TProductMeshSources = {
    cuttingShapeRepository: ICuttingShapeRepository;
    productStateRepository: IProductStateRepository;
};

// Null when no product is loaded, which is a valid state until initStore resolves.
function buildProductMeshParamsFromState(sources: TProductMeshSources, context: TProductMeshContext): TProductMeshParams | null {
    const product = sources.productStateRepository.getProduct();
    if (!product) {
        return null;
    }

    return buildProductMeshParams(product, sources.cuttingShapeRepository.get()?.url ?? null, context);
}

function buildProductMeshParams(product: Product, cuttingShapeUrl: string | null, context: TProductMeshContext): TProductMeshParams {
    if (product.category === EProductCategory.Glass) {
        return {
            category: EProductCategory.Glass,
            meshBaseName: product.meshBaseName ?? undefined,
            modelUrl: product.model ? context.fileUrl(product.model) : undefined,
            productType: context.urlProductType,
        };
    }

    return {
        cardForm: product.cardForm ?? EProductCardForm.Folded,
        category: EProductCategory.Card,
        cuttingPathUrl: product.cuttingPathUuid ? cuttingShapeUrl : null,
        productFormat: product.format ?? EProductFormat.RECTO_VERSO,
        productHeightInMm: product.height,
        productWidthInMm: product.width,
    };
}

export { buildProductMeshParams, buildProductMeshParamsFromState };
export type { TProductMeshContext, TProductMeshSources };
